import React, { useEffect, useMemo, useState } from "react";

/**
* Card Radio Buttons
* ------------------
* A set of radio buttons that are displayed as cards.
* Fitting for a small selection of options, which also require a short description.
* 
* Example: used in AddEditTeamMember.tsx to select the role of a team member.
*/

type CardRadioOption = {
  label: string;
  description: string;
  value: string | number;
  disabled?: boolean;
  id?: string | number;
};

type CardRadioButtonsProps = {
  options: CardRadioOption[];
  value?: string | number;
  defaultValue?: string | number;
  onChange?: (value: string | number, option: CardRadioOption, index: number) => void;
  groupLabel?: string;
  className?: string;
};

export const CardRadioButtons: React.FC<CardRadioButtonsProps> = ({
  options,
  value,
  defaultValue,
  onChange,
  groupLabel = "Options",
  className,
}) => {
  const normalizedOptions = useMemo(() => options ?? [], [options]);

  const getInitialValue = (): string | number => {
    if (typeof value === "string" || typeof value === "number") return value;
    if (typeof defaultValue === "string" || typeof defaultValue === "number") return defaultValue;
    if (normalizedOptions.length === 0) return "";
    const first = normalizedOptions[0];
    return first.value as string | number;
  };

  const [internalValue, setInternalValue] = useState<string | number>(getInitialValue());

  useEffect(() => {
    if (typeof value === "string" || typeof value === "number") setInternalValue(value);
  }, [value]);

  const currentValue = typeof value === "string" || typeof value === "number" ? value : internalValue;

  const handleSelect = (option: CardRadioOption, index: number): void => {
    const selectedValue = option.value;
    if (option.disabled) return;
    if (String(currentValue) === String(selectedValue)) return;

    if (!(typeof value === "string" || typeof value === "number")) setInternalValue(selectedValue);
    onChange?.(selectedValue, option, index);
  };

  const getCardClasses = (isSelected: boolean, isDisabled: boolean): string => {
    const base =
      "relative flex items-start gap-3 rounded-[16px] p-4 border transition-colors cursor-pointer outline-none";
    const state = isSelected
      ? "bg-white border-partnerPrimary ring-2 ring-partnerPrimaryLight shadow-sm"
      : "bg-white border-gray-300 hover:border-gray-400";
    const disabled = isDisabled ? "opacity-60 cursor-not-allowed" : "";
    return `${base} ${state} ${disabled}`;
  };

  if (!normalizedOptions.length) {
    return null;
  }

  return (
    <div
      role="radiogroup"
      aria-label={groupLabel}
      className={`grid grid-cols-1 sm:grid-cols-2 gap-3 ${className || ""}`}
    >
      {normalizedOptions.map((option, index) => {
        const optionValue = option.value;
        const isSelected = String(currentValue) === String(optionValue);
        const isDisabled = Boolean(option.disabled);

        return (
          <div
            key={option.id ?? optionValue ?? index}
            role="radio"
            aria-checked={isSelected}
            aria-disabled={isDisabled}
            tabIndex={isDisabled ? -1 : 0}
            className={getCardClasses(isSelected, isDisabled)}
            onClick={() => handleSelect(option, index)}
            onKeyDown={(e) => {
              if (isDisabled) return;
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleSelect(option, index);
              }
            }}
          >
            <div className="pt-1">
              <span
                aria-hidden="true"
                className={`flex h-5 w-5 items-center justify-center rounded-full border ${
                  isSelected ? "border-partnerPrimary bg-partnerPrimaryLight" : "border-gray-900 bg-white"
                }`}
              >
                <span
                  className={`h-2.5 w-2.5 rounded-full ${
                    isSelected ? "bg-partnerPrimary" : "bg-transparent"
                  }`}
                />
              </span>
            </div>

            <div className="flex-1">
              <div className="text-sm font-semibold text-black">{option.label}</div>
              <p className="mt-1 text-sm leading-5 text-gray-500">{option.description}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
};